import { delay, of, Subject, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	type OnDestroy,
	type OnInit,
	output,
	ViewEncapsulation
} from '@angular/core';

import { IconComponent } from '@components/atoms/icon/icon.component';
import type { IconName } from '@components/atoms/icon/models';

export enum NotificationMessageModeOptions {
	INFO = 'info',
	WARNING = 'warning',
	ERROR = 'error',
	SUCCESS = 'success'
}

export type NotificationMessageMode = `${NotificationMessageModeOptions}`;

enum NotificationMessageStyleOptions {
	DEFAULT = 'a-notification-message',
	INFO = 'a-notification-message--info',
	WARNING = 'a-notification-message--warning',
	ERROR = 'a-notification-message--error',
	SUCCESS = 'a-notification-message--success'
}

@Component({
	selector: 'a-notification-message',
	templateUrl: './notification-message.component.html',
	styleUrls: ['../../../styles/6-atoms/_notification-message.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule, IconComponent],
	providers: []
})
export class NotificationMessageComponent implements OnInit, OnDestroy {
	title = input.required<string>();
	mode = input.required<NotificationMessageMode>();
	message = input.required<string>();
	expiration = input<number | null>(null);
	closable = input<boolean>(false);
	closed = output<void>();
	private destroy$ = new Subject<void>();

	protected icon = computed(() => this.getIcon(this.mode()));

	protected notificationmessageClasses = computed(() =>
		this.getNotificationMessageClasses(this.mode())
	);

	ngOnInit() {
		this.setupExpiration();
	}

	private getNotificationMessageClasses(
		mode: NotificationMessageMode
	): Record<NotificationMessageStyleOptions, boolean> {
		return {
			[NotificationMessageStyleOptions.DEFAULT]: true,
			[NotificationMessageStyleOptions.INFO]: mode === NotificationMessageModeOptions.INFO,
			[NotificationMessageStyleOptions.WARNING]:
				mode === NotificationMessageModeOptions.WARNING,
			[NotificationMessageStyleOptions.ERROR]: mode === NotificationMessageModeOptions.ERROR,
			[NotificationMessageStyleOptions.SUCCESS]:
				mode === NotificationMessageModeOptions.SUCCESS
		};
	}

	private getIcon(mode: NotificationMessageMode): IconName {
		if (mode === NotificationMessageModeOptions.SUCCESS) return 'checkCircle';
		if (mode === NotificationMessageModeOptions.ERROR) return 'timesCircle';
		if (mode === NotificationMessageModeOptions.WARNING) return 'warning';
		if (mode === NotificationMessageModeOptions.INFO) return 'infoCircle';

		return 'timesCircle';
	}

	private setupExpiration() {
		/* TODO: Implementacion para añadir estilos del porcentaje de tiempo restante de la notificacion
		if (this.expiration() !== null) {
			const startTime = Date.now();
			const totalTime = this.expiration();
			const timeInterval = 1000;

			interval(timeInterval)
				.pipe(takeUntil(this.destroy$))
				.subscribe(() => {
					const elapsed = Date.now() - startTime;
					const percentage = Math.min(100, (elapsed / totalTime) * 100);

					if (percentage > 100) {
						this.closeNotification();
					}
				});
		} */
		if (this.expiration() !== null) {
			of(void 0)
				.pipe(delay(this.expiration()), takeUntil(this.destroy$))
				.subscribe(() => this.closeNotification());
		}
	}

	protected closeNotification() {
		this.destroy$.next();
		this.destroy$.complete();
		this.closed.emit();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
